/**
 * Reviews Showcase — slider de una fila, tarjetas de tamaño fijo.
 * Toda la tarjeta es clicable y abre la reseña completa en un modal.
 */
class ReviewsShowcase {
  constructor(container) {
    this.container = container;
    this.wrapper = container.querySelector('[data-rvs-wrapper]');
    this.emptyMsg = container.querySelector('[data-rvs-empty]');
    this.modal = container.querySelector('[data-rvs-modal]');
    if (!this.wrapper) return;

    this.apiUrl = container.dataset.apiUrl;
    this.perPage = parseInt(container.dataset.perPage) || 10;
    this.productId = container.dataset.productId;
    this.filterByProduct = container.dataset.filterByProduct === 'true';
    this.cardScheme = container.dataset.cardScheme || '';
    this.cdnBase = 'https://babybelegal.com/cdn/shop/files/';

    this.bindModal();
    this.init();
  }

  async init() {
    if (!this.apiUrl) return;
    try {
      const reviews = await this.fetchReviews();
      if (reviews && reviews.length > 0) {
        this.render(reviews);
        this.initSwiper();
      } else {
        this.showEmpty();
      }
    } catch (err) {
      console.error('Error loading reviews:', err);
      this.showEmpty();
    }
  }

  async fetchReviews() {
    let url = this.apiUrl;
    if (this.filterByProduct && this.productId) {
      url = `${this.apiUrl}/product/external/${this.productId}`;
    }
    const params = new URLSearchParams({ per_page: this.perPage, page: 1 });
    const response = await fetch(`${url}?${params.toString()}`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    if (!data.success) throw new Error('API returned unsuccessful response');
    return data.reviews || [];
  }

  buildImageUrl(handle, width = 140) {
    if (!handle) return '';
    if (handle.startsWith('http://') || handle.startsWith('https://')) return handle;
    return `${this.cdnBase}${handle}?width=${width}`;
  }

  render(reviews) {
    this.wrapper.innerHTML = '';
    reviews.forEach((review) => this.wrapper.appendChild(this.buildSlide(review)));
  }

  showEmpty() {
    this.wrapper.innerHTML = '';
    if (this.emptyMsg) this.emptyMsg.hidden = false;
  }

  buildSlide(review) {
    const rating = Math.min(5, Math.max(1, parseInt(review.rating) || 5));
    const text = review.body || '';
    const name = review.reviewer?.name || '';
    const imageUrl = this.buildImageUrl(review.author_picture);
    const initial = name ? name.charAt(0).toUpperCase() : '?';

    const slide = document.createElement('div');
    slide.className = 'swiper-slide';
    slide.innerHTML = `
      <div class="rvs__card ${this.cardScheme}" role="button" tabindex="0" aria-label="Leer reseña completa de ${this.escapeHTML(name)}">
        <div class="rvs__rating">
          <svg width="13" height="13" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M10 1.5l2.6 5.6 6.1.6-4.6 4.1 1.3 6-5.4-3.1-5.4 3.1 1.3-6L1.3 7.7l6.1-.6z"/></svg>
          <span>${rating}.00</span>
        </div>
        ${text ? `<p class="rvs__card-text body-base">${this.escapeHTML(text)}</p>` : ''}
        <div class="rvs__author">
          <div class="rvs__avatar">${imageUrl ? `<img src="${imageUrl}" alt="${this.escapeHTML(name)}" loading="lazy">` : initial}</div>
          <div class="rvs__author-name body-small">${this.escapeHTML(name)}</div>
        </div>
      </div>
    `;

    const cardEl = slide.querySelector('.rvs__card');
    const openHandler = () => this.openModal({ rating, text, name, imageUrl, initial });
    cardEl.addEventListener('click', openHandler);
    cardEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openHandler();
      }
    });

    return slide;
  }

  bindModal() {
    if (!this.modal) return;
    this.modal.querySelectorAll('[data-rvs-modal-close]').forEach((el) => {
      el.addEventListener('click', () => this.closeModal());
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.modal.hidden) this.closeModal();
    });
  }

  openModal({ rating, text, name, imageUrl, initial }) {
    if (!this.modal) return;
    this.modal.querySelector('[data-rvs-modal-rating]').textContent = `${rating}.00`;
    this.modal.querySelector('[data-rvs-modal-text]').textContent = text;
    this.modal.querySelector('[data-rvs-modal-name]').textContent = name;
    const avatar = this.modal.querySelector('[data-rvs-modal-avatar]');
    avatar.innerHTML = imageUrl ? `<img src="${imageUrl}" alt="${this.escapeHTML(name)}" loading="lazy">` : initial;
    this.modal.hidden = false;
  }

  closeModal() {
    if (this.modal) this.modal.hidden = true;
  }

  initSwiper() {
    const slider = this.container.querySelector('[data-rvs-slider]');
    if (!slider || typeof Swiper === 'undefined') return;

    if (slider.swiper) slider.swiper.destroy(true, true);

    const navigation = this.container.querySelector('.rvs__navigation');
    const pagination = this.container.querySelector('[data-rvs-pagination]');

    setTimeout(() => {
      new Swiper(slider, {
        slidesPerView: 1.15,
        slidesPerGroup: 1,
        spaceBetween: 24,
        mousewheel: { forceToAxis: true },
        breakpoints: {
          750: { slidesPerView: 2.2, slidesPerGroup: 1 },
          1200: { slidesPerView: 3, slidesPerGroup: 1 },
        },
        pagination: pagination ? { el: pagination, type: 'bullets', clickable: true } : undefined,
        navigation: navigation
          ? {
              nextEl: navigation.querySelector('.swiper-button-next'),
              prevEl: navigation.querySelector('.swiper-button-prev'),
            }
          : undefined,
      });
    }, 10);
  }

  escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.rvs[data-api-url]').forEach((el) => new ReviewsShowcase(el));
});

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const el = event.target.querySelector('.rvs[data-api-url]');
    if (el) new ReviewsShowcase(el);
  });
}